import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

// We deliberately do NOT poll /payment/status per-session anymore — that's the
// flow that failed in production.  Instead we call /payment/recover which
// asks Stripe directly about EVERY checkout session this user has started,
// and flips has_paid=true the moment any of them are confirmed.  One reliable
// path beats 90 fragile ones.
const MAX_RECOVER_ATTEMPTS = 20;     // 20 × 3 s ≈ 60 s of patience
const RECOVER_INTERVAL = 3000;
const REQ_TIMEOUT_MS = 15000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    setMessage(t("paysucc_recovering"));
  }, [t]);

  const goToDashboard = async () => {
    await refresh();
    setTimeout(() => navigate("/dashboard", { replace: true }), 1000);
  };

  // Calls the backend recover endpoint.  Returns true if user is now paid.
  const callRecover = async () => {
    try {
      const { data } = await api.post("/payment/recover", null, { timeout: REQ_TIMEOUT_MS });
      return Boolean(data?.has_paid);
    } catch {
      return false;
    }
  };

  // Background loop: keep asking the backend to reconcile with Stripe until the
  // user is unlocked, or we hit MAX_RECOVER_ATTEMPTS.
  const recoverLoop = async (n = 0) => {
    if (cancelledRef.current) return;
    setAttempt(n + 1);
    const ok = await callRecover();
    if (cancelledRef.current) return;
    if (ok) {
      setStatus("paid");
      setMessage(t("paysucc_confirmed"));
      await goToDashboard();
      return;
    }
    if (n + 1 >= MAX_RECOVER_ATTEMPTS) {
      setStatus("slow");
      setMessage(t("paysucc_slow"));
      return;
    }
    setTimeout(() => recoverLoop(n + 1), RECOVER_INTERVAL);
  };

  useEffect(() => {
    cancelledRef.current = false;
    // Already unlocked? Just go in.
    if (user?.has_paid) {
      setStatus("paid");
      setMessage(t("paysucc_confirmed"));
      setTimeout(() => navigate("/dashboard", { replace: true }), 600);
      return;
    }
    // No session id in URL — still try recover once (covers refresh / back-button cases).
    recoverLoop(0);
    return () => { cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual "I was charged — recover now" button.
  const manualRecover = async () => {
    setStatus("checking");
    setMessage(t("paysucc_recovering"));
    setAttempt(0);
    const ok = await callRecover();
    if (ok) {
      setStatus("paid");
      setMessage(t("paysucc_confirmed"));
      await goToDashboard();
    } else {
      setStatus("slow");
      setMessage(t("paysucc_slow"));
    }
  };

  const titleKey = {
    paid: "paysucc_title_paid",
    failed: "paysucc_title_failed",
    expired: "paysucc_title_expired",
    slow: "paysucc_title_slow",
    checking: "paysucc_title_checking",
  }[status];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black flex items-center justify-center px-5 py-10" data-testid="payment-success-page">
      <div className="w-full max-w-lg bg-white border-2 border-black brutal-shadow p-8 text-center">
        <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">{t("paysucc_tag")}</div>
        <h1 className="mt-2 font-serif-display text-4xl text-black leading-tight" style={{ fontWeight: 800 }} data-testid="payment-status-title">
          {t(titleKey)}
        </h1>
        <p className="mt-4 text-black" style={{ fontWeight: 700 }} data-testid="payment-status-message">{message}</p>

        {status === "checking" && (
          <div className="mt-6">
            <div className="h-1 w-full bg-black/10 overflow-hidden">
              <div className="h-full bg-[#E00000] bar-grow" style={{ width: "70%" }} />
            </div>
            <div className="mt-2 font-mono-civic text-[10px] uppercase tracking-widest text-black/60">
              {t("paysucc_attempt")} {attempt} {t("paysucc_of")} {MAX_RECOVER_ATTEMPTS}
            </div>
            {/* Always-visible manual escape hatch (no waiting 3 min before showing it) */}
            <div className="mt-6 pt-6 border-t border-black/20">
              <button
                onClick={manualRecover}
                className="w-full bg-[#E00000] text-white uppercase tracking-widest py-3 border-2 border-[#E00000]"
                style={{ fontWeight: 900 }}
                data-testid="payment-recover-btn-inline"
              >
                {t("paysucc_recover_btn")}
              </button>
              <Link to="/dashboard" className="block mt-3 underline text-sm" style={{ fontWeight: 800 }} data-testid="payment-go-dashboard-inline">
                {t("paysucc_go_dashboard")}
              </Link>
            </div>
          </div>
        )}

        {status === "slow" && (
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={manualRecover}
              className="bg-[#E00000] text-white uppercase tracking-widest py-3 border-2 border-[#E00000]"
              style={{ fontWeight: 900 }}
              data-testid="payment-recover-btn"
            >
              {t("paysucc_recover_btn")}
            </button>
            <Link
              to="/dashboard"
              className="bg-black text-white uppercase tracking-widest py-3 border-2 border-black"
              style={{ fontWeight: 900 }}
              data-testid="payment-go-dashboard"
            >
              {t("paysucc_go_dashboard")}
            </Link>
            <Link to="/home" className="text-black underline" style={{ fontWeight: 800 }}>
              {t("paysucc_back_home_long")}
            </Link>
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div className="mt-8 flex flex-col gap-3">
            <button
              onClick={manualRecover}
              className="bg-[#E00000] text-white uppercase tracking-widest py-3 border-2 border-[#E00000]"
              style={{ fontWeight: 900 }}
              data-testid="payment-recover-btn-fail"
            >
              {t("paysucc_recover_btn")}
            </button>
            <Link
              to="/paywall"
              className="bg-black text-white uppercase tracking-widest py-3 border-2 border-black"
              style={{ fontWeight: 900 }}
              data-testid="payment-retry-btn"
            >
              {t("paysucc_try_again")}
            </Link>
            <Link to="/home" className="text-black underline" style={{ fontWeight: 800 }}>{t("paysucc_back_home")}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
